/**
 * Freeze V2 — Fertilization Grid (master grid)
 *
 * AG Grid master-detail with fertilization rows as master and embryo
 * tables (live + temp) as detail, on the client-side row model.
 */
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import type {
  CellEditingStartedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  FirstDataRenderedEvent,
  GridReadyEvent,
  RowClassParams,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact, type CustomDetailCellRendererProps } from 'ag-grid-react';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { WithLocalStorage } from '@/components/hoc';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { RowIdParams } from '@/grid/types';
import { PrimaryContext } from '@/services';
import type {
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
} from '@/services/freeze';
import { useContextMenu } from '@/utils/context-menu';

import {
  useFreezeForm,
  useFreezeGrid,
  useFreezeSelection,
} from '../../contexts';
import { fertilizationColDefs } from '../../utils/columnDefs';
import { MOBILE_BREAKPOINT } from '../../utils/constants';
import { sameIds } from '../../utils/selection';
import { EmbryoDetailRenderer } from './EmbryoDetailRenderer';

type FertilizationGridProps = {
  fertilizations: FertilizationListFreezeResponse[];
  embryosByFert: Map<string, FertAndEmbryosResponse>;
  isLoading: boolean;
  fertilizationSiteId: string | null;
  onFertCellValueChanged: (event: CellValueChangedEvent) => void;
  onCellKeyDown: (event: CellKeyDownEvent) => void;
  onRefreshEmbryos: () => void;
};

const rowClassRules = {
  'fresh-shipment-form__row-shaded': (params: RowClassParams) =>
    params.rowIndex % 2 === 1,
};

export const FertilizationGrid = memo(function FertilizationGrid({
  fertilizations,
  embryosByFert,
  isLoading,
  fertilizationSiteId,
  onFertCellValueChanged,
  onCellKeyDown,
  onRefreshEmbryos,
}: FertilizationGridProps) {
  const dispatch = useDispatch();
  const { themeMode } = useContext(PrimaryContext);
  const { setGridApi, isUpdateRunning } = useFreezeGrid();
  const { batchForm } = useFreezeForm();
  const { dispatch: selectionDispatch, fertilizationIds } =
    useFreezeSelection();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const gridRef = useRef<AgGridReact<FertilizationListFreezeResponse>>(null);

  const selectionRef = useRef(fertilizationIds);
  selectionRef.current = fertilizationIds;
  const predictionTechRef = useRef(batchForm.predictionTechId);
  predictionTechRef.current = batchForm.predictionTechId;
  const defaultGridOptions = useDefaultGridOptions();
  const { getContextMenuItems } = useContextMenu({ idKey: 'fertId' });

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api);
      if (isLoading) {
        event.api.showLoadingOverlay();
      }
    },
    [setGridApi, isLoading],
  );

  const syncFertSelection = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.forEachNode((node) => {
      const id = node.data?.fertId;
      const shouldSelect = !!id && selectionRef.current.includes(id);
      if (node.isSelected() !== shouldSelect) {
        node.setSelected(shouldSelect, false);
      }
    });
  }, []);

  useEffect(() => {
    syncFertSelection();
  }, [fertilizationIds, syncFertSelection]);

  const onFirstDataRendered = useCallback(
    (_event: FirstDataRenderedEvent) => {
      syncFertSelection();
    },
    [syncFertSelection],
  );

  const handleCellEditingStarted = useCallback(
    (event: CellEditingStartedEvent) => {
      const restrictedFields = [
        'cleaved',
        'predictedGood',
        'predictedVeryLow',
        'heldAtTogToFreeze',
      ];
      const field = event.column.getColDef().field;
      if (
        field &&
        restrictedFields.includes(field) &&
        !predictionTechRef.current
      ) {
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Prediction Tech Required',
            detail: 'Set Prediction Tech before editing this field.',
            life: 3000,
          }),
        );
        event.api.stopEditing(true);
      }
    },
    [dispatch],
  );

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      const ids = (
        event.api.getSelectedRows() as FertilizationListFreezeResponse[]
      )
        .map((f) => f.fertId)
        .filter((id): id is string => !!id);
      if (!sameIds(ids, selectionRef.current)) {
        selectionDispatch({ type: 'SET_FERT_IDS', ids });
      }
    },
    [selectionDispatch],
  );

  // Stable context ref so detail panels don't re-render on parent updates
  const stableContextRef = useRef<Record<string, unknown>>({});
  Object.assign(stableContextRef.current, {
    embryosByFert,
    isUpdateRunning,
    onRefreshEmbryos,
  });

  const detailCellRenderer = useCallback(
    (params: CustomDetailCellRendererProps) => (
      <EmbryoDetailRenderer
        {...params}
        embryosByFert={
          stableContextRef.current.embryosByFert as Map<
            string,
            FertAndEmbryosResponse
          >
        }
        onRefreshEmbryos={
          stableContextRef.current.onRefreshEmbryos as () => void
        }
      />
    ),
    [],
  );

  const gridContext = useMemo(
    () => ({ rawFertilizationSiteId: fertilizationSiteId }),
    [fertilizationSiteId],
  );

  const gridClassName = useMemo(
    () =>
      `ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-full w-full medium-font__grid`,
    [themeMode],
  );

  return (
    <div className="min-h-0 flex-1">
      {isUpdateRunning && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
          <div className="text-sm font-medium text-gray-600">
            <i className="pi pi-spin pi-spinner mr-2" />
            Updating...
          </div>
        </div>
      )}
      <WithLocalStorage storageKey="freeze-v2-grid">
        <AgGridReact<FertilizationListFreezeResponse>
          ref={gridRef}
          className={gridClassName}
          rowData={fertilizations}
          columnDefs={fertilizationColDefs}
          defaultColDef={{
            ...defaultColDef,
            minWidth: isMobile ? 70 : undefined,
            // cellClassRules (not cellClass) so column types like numericColumn,
            // which supply their own cellClass, don't override the marker.
            cellClassRules: {
              'editable-cell': (params) => {
                const cd = params.colDef;
                if (
                  typeof cd.cellRenderer === 'string' &&
                  cd.cellRenderer.startsWith('ag')
                ) {
                  return false;
                }
                const e = cd.editable;
                return typeof e === 'function'
                  ? (e as (p: typeof params) => boolean)(params)
                  : !!e;
              },
            },
          }}
          gridOptions={defaultGridOptions}
          context={gridContext}
          masterDetail
          isRowMaster={() => true}
          detailCellRenderer={detailCellRenderer}
          detailRowAutoHeight
          keepDetailRows
          keepDetailRowsCount={20}
          rowSelection="multiple"
          suppressRowClickSelection
          rowClassRules={rowClassRules}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          onCellEditingStarted={handleCellEditingStarted}
          onCellValueChanged={onFertCellValueChanged}
          onSelectionChanged={handleSelectionChanged}
          onCellKeyDown={onCellKeyDown}
          getContextMenuItems={getContextMenuItems}
          getRowId={(params: RowIdParams<FertilizationListFreezeResponse>) =>
            params.data.fertId ?? params.data.plateNumber?.toString() ?? ''
          }
          rowHeight={40}
          rowBuffer={20}
          animateRows={false}
          suppressDragLeaveHidesColumns
          suppressScrollOnNewData
        />
      </WithLocalStorage>
    </div>
  );
});
